export const taskParameterFieldsCollection: Record<string, Array<string>> = {
  common: ['extra', 'priority', 'model'],
  /** Common parameters for Generator + i2vPro */
  generatorAndI2vProBase: ['width', 'height', 'prompts', 'modelId'],
  /** Workflow only; `input` is explicitly stripped  */
  workflow: ['workflowVersionId', 'workflowName', 'workflowId'],
  i2vPro: ['i2vPro'],
  referenceVideo: ['referenceVideo'],
  t2i2v: ['t2i2v'],
  i2vChannel: ['channel'],
  /** Below are Generator only */
  generatorBase: [
    'negativePrompts',
    'samplingSteps',
    'samplingMethod',
    'cfgScale',
    'seed',
    'clipSkip',
    'inferenceProfile',
  ],
  batchGen: ['batchSize'],
  // For task submission
  i2iPreProcess: ['strength'],
  // For task fetching
  i2iPostProcess: ['strength', 'mediaId', 'maskMediaId'],
  enlarge: ['enlarge', 'enlargeModel'],
  upscale: [
    'upscale',
    'upscaleDenoisingStrength',
    'upscaleDenoisingSteps',
    'upscaleSampler',
    'enableTile',
  ],
  lora: ['lora', 'loraParameters'],
  latentCouple: ['latentCouple'],
  aDetailer: ['enableADetailer'],
  vaeModel: ['vaeModelId'],
  animateDiff: ['animateDiff'],
  inferenceConfig: ['inferenceConfig'],
  chat: ['chat'],
  promptHelperConfig: ['promptHelper'],

  /** Should be categorized later */
  extraFeatures: [
    'lora',
    'controlNets',
    'ipAdapter',
    'qualityTag',
    'styleMix',
    'lightning',
    'modelStyle',
    'watermark',
  ],
} as const

/**
 * Minimal subset the task rail / favorites list reads at render time:
 * type discriminators, indicator badges, output-media derivation, and
 * model-version prefetch. Anything an editor only needs at import time
 * is intentionally omitted — the import path refetches the full task.
 */
export const taskParameterFieldsForRailRender = [
  ...taskParameterFieldsCollection.common,
  'modelId',
  'workflowId',
  'batchSize',
  'animateDiff',
  'i2vPro',
  'referenceVideo',
  't2i2v',
  'chat',
  'upscale',
  'enlarge',
  'mediaId',
]

/**
 * Synthesizes a legacy `parameters` shape on a task that came back from
 * a typed-list query (`TaskWithMediaTyped`), so helpers / `isXxxTask`
 * matchers / `getBaseMediaIdFromTask` etc. that read `task.parameters.*`
 * keep working without a parallel rename. Aliases `modelVersionId →
 * modelId` since the typed surface uses the accurate name (#11089) but
 * the JSON source key — and the legacy helpers — use `modelId`. Tasks
 * that already carry `parameters` (e.g. newly-created tasks pushed
 * through `setMyTasks` from the generate flow, which use the
 * `TaskDetail` fragment) pass through unchanged.
 */
export function withSyntheticParameters<
  T extends {
    parameters?: unknown
    typedParameters?:
      | (Record<string, unknown> & { modelVersionId?: unknown })
      | null
  },
>(task: T): T {
  if (task.parameters) return task
  if (!task.typedParameters) return task
  const { modelVersionId, ...rest } = task.typedParameters
  const parameters =
    modelVersionId !== undefined ? { ...rest, modelId: modelVersionId } : rest
  return { ...task, parameters }
}

/**
 * Only includes necessary fields to reduce response payload size
 */
export const taskParameterFieldsForTaskList = [
  ...taskParameterFieldsCollection.common,
  ...taskParameterFieldsCollection.generatorAndI2vProBase,
  ...taskParameterFieldsCollection.workflow,
  ...taskParameterFieldsCollection.generatorBase,
  ...taskParameterFieldsCollection.i2vPro,
  ...taskParameterFieldsCollection.referenceVideo,
  ...taskParameterFieldsCollection.t2i2v,
  ...taskParameterFieldsCollection.i2vChannel,
  ...taskParameterFieldsCollection.chat,
  /**
   * Generator tasks
   */
  ...taskParameterFieldsCollection.i2iPostProcess,
  ...taskParameterFieldsCollection.lora,
  ...taskParameterFieldsCollection.batchGen,
  ...taskParameterFieldsCollection.enlarge,
  ...taskParameterFieldsCollection.upscale,
  ...taskParameterFieldsCollection.latentCouple,
  ...taskParameterFieldsCollection.aDetailer,
  ...taskParameterFieldsCollection.animateDiff,
  ...taskParameterFieldsCollection.vaeModel,
  ...taskParameterFieldsCollection.inferenceConfig,
  ...taskParameterFieldsCollection.promptHelperConfig,

  /** Extra features */
  ...taskParameterFieldsCollection.extraFeatures,
]
